import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Toolbar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Collapse,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add,
  Search,
  ExpandMore,
  ExpandLess,
  Route,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Routing, RoutingOperation } from '../../../store/slices/masterDataSlice';

const OUTPUT_TYPE_COLORS: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  primary: 'success',
  coproduct: 'info',
  byproduct: 'warning',
  scrap: 'error',
  loss: 'default',
};

const RoutingsTable: React.FC = () => {
  const { routings, routingsLoading, workCenters, items } = useSelector(
    (state: RootState) => state.masterData
  );

  const [searchText, setSearchText] = useState('');
  const [expandedRoutingId, setExpandedRoutingId] = useState<string | null>(null);
  const [expandedOpId, setExpandedOpId] = useState<string | null>(null);
  const [routingDialogOpen, setRoutingDialogOpen] = useState(false);

  const filteredRoutings = routings.filter(
    (r) =>
      !searchText ||
      r.code.toLowerCase().includes(searchText.toLowerCase()) ||
      r.name.toLowerCase().includes(searchText.toLowerCase()) ||
      r.itemCode.toLowerCase().includes(searchText.toLowerCase())
  );

  const toggleExpand = (id: string) => {
    setExpandedRoutingId(expandedRoutingId === id ? null : id);
    setExpandedOpId(null);
  };

  const toggleOpExpand = (id: string) => {
    setExpandedOpId(expandedOpId === id ? null : id);
  };

  const renderOperationIO = (op: RoutingOperation, batchSize?: number) => (
    <Box sx={{ ml: 4, my: 1, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        Material flow for this operation — quantities are per standard batch{batchSize ? ` of ${batchSize}` : ''}
      </Typography>
      <Box display="flex" gap={3} flexWrap="wrap">
        <Box flex={1} minWidth={320}>
          <Typography variant="subtitle2" gutterBottom>
            Inputs ({op.inputs.length})
          </Typography>
          {op.inputs.length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              No inputs defined for this operation.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty / Batch</TableCell>
                  <TableCell>UOM</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Scrap%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {op.inputs.map((inp) => (
                  <TableRow key={inp.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {inp.productCode}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {inp.productName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{Number(inp.quantityPer)}</TableCell>
                    <TableCell>{inp.uom}</TableCell>
                    <TableCell>
                      {inp.sourceType === 'prior_operation' ? (
                        <Chip label={`← Op ${inp.sourceOperationSequence ?? '?'}`} size="small" color="secondary" variant="outlined" />
                      ) : (
                        <Chip label="Stores" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">{(Number(inp.scrapFactor) * 100).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
        <Box flex={1} minWidth={320}>
          <Typography variant="subtitle2" gutterBottom>
            Outputs ({op.outputs.length})
          </Typography>
          {op.outputs.length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              No outputs defined for this operation.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty / Batch</TableCell>
                  <TableCell>UOM</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Flow</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {op.outputs.map((out) => (
                  <TableRow key={out.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {out.productCode}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {out.productName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{Number(out.quantityPer)}</TableCell>
                    <TableCell>{out.uom}</TableCell>
                    <TableCell>
                      <Chip
                        label={out.outputType.toUpperCase()}
                        size="small"
                        color={OUTPUT_TYPE_COLORS[out.outputType] ?? 'default'}
                        variant={out.outputType === 'primary' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {out.feedsNextOperation ? (
                        <Chip label={`→ Op ${out.nextOperationSequence ?? 'next'}`} size="small" color="secondary" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          To stock
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>
    </Box>
  );

  const getStatusColor = (status: Routing['status']): 'success' | 'default' | 'info' => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search routings..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <Box flex={1} />
          <Button variant="contained" startIcon={<Add />} onClick={() => setRoutingDialogOpen(true)}>
            Add Routing
          </Button>
        </Toolbar>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Operations</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoutings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Alert severity="info">No routings found.</Alert>
                  </TableCell>
                </TableRow>
              )}
              {filteredRoutings.map((routing) => (
                <React.Fragment key={routing.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(routing.id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedRoutingId === routing.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {routing.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{routing.name}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{routing.itemName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {routing.itemCode}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={`v${routing.version}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${routing.operations.length} ops`}
                        size="small"
                        icon={<Route />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={routing.status.toUpperCase()}
                        color={getStatusColor(routing.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>

                  {/* Operations Detail Row */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedRoutingId === routing.id} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Operation Sequence
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                            Click an operation to see its material inputs and outputs.
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell width={32} />
                                <TableCell>Seq</TableCell>
                                <TableCell>Op #</TableCell>
                                <TableCell>Operation Name</TableCell>
                                <TableCell>Work Center</TableCell>
                                <TableCell align="center">Materials</TableCell>
                                <TableCell align="right">Setup (min)</TableCell>
                                <TableCell align="right">Run Time</TableCell>
                                <TableCell>Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {routing.operations
                                .slice()
                                .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                                .map((op) => (
                                  <React.Fragment key={op.id}>
                                    <TableRow
                                      hover
                                      sx={{ cursor: 'pointer' }}
                                      onClick={() => toggleOpExpand(op.id)}
                                    >
                                      <TableCell>
                                        <IconButton size="small">
                                          {expandedOpId === op.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                        </IconButton>
                                      </TableCell>
                                      <TableCell>
                                        <Box
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                          }}
                                        >
                                          {op.sequenceNumber}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                          {op.operationNumber}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{op.operationName}</TableCell>
                                      <TableCell>
                                        <Box>
                                          <Typography variant="body2">{op.workCenterName}</Typography>
                                          <Typography variant="caption" color="textSecondary">
                                            {op.workCenterCode}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip
                                          label={`${op.inputs.length} in / ${op.outputs.length} out`}
                                          size="small"
                                          variant="outlined"
                                          color={op.inputs.length + op.outputs.length > 0 ? 'primary' : 'default'}
                                        />
                                      </TableCell>
                                      <TableCell align="right">{op.setupTimeMinutes} min</TableCell>
                                      <TableCell align="right">
                                        {op.runTimeMinutes} {op.runTimeUom}
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption" color="textSecondary">
                                          {op.notes || '—'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={expandedOpId === op.id} timeout="auto" unmountOnExit>
                                          {renderOperationIO(op, routing.batchSize)}
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Routing Dialog */}
      <Dialog open={routingDialogOpen} onClose={() => setRoutingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Route />
            Add Routing
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField label="Routing Code *" size="small" sx={{ flex: 1 }} />
              <TextField label="Version" size="small" defaultValue="1.0" sx={{ width: 80 }} />
            </Box>
            <TextField label="Routing Name *" size="small" fullWidth />
            <FormControl size="small" fullWidth>
              <InputLabel>Item *</InputLabel>
              <Select label="Item *" defaultValue="">
                <MenuItem value="">Select item...</MenuItem>
                {items
                  .filter((i) => i.type === 'finished_good' || i.type === 'semi_finished')
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField label="Description" size="small" fullWidth multiline rows={2} />
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              After creating the routing, add operations via the operation sequence editor.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoutingDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setRoutingDialogOpen(false)}>
            Create Routing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoutingsTable;
